import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, ImageOff, LayoutDashboard, QrCode, Search, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { assetsApi } from '@/api/endpoints'
import { Badge, TrcLogoBadge, UserProfileModal } from '@/components/ui'
import { useAuth } from '@/features/auth/AuthContext'
import { useLookups } from '@/hooks/useLookups'
import { greeting, humanise } from '@/lib/format'

export default function HomePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: lookups } = useLookups()
  const [profileOpen, setProfileOpen] = useState(false)

  const recent = useQuery({
    queryKey: ['assets', { limit: 5 }],
    queryFn: () => assetsApi.list({ limit: 5, offset: 0 }),
  })

  const firstName = user?.full_name?.split(' ')[0] ?? ''
  const initials = user?.full_name
    ? user.full_name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?'

  return (
    <div className="flex h-[calc(100dvh-8.5rem)] md:h-[calc(100vh-5.5rem)] max-h-[calc(100dvh-8.5rem)] md:max-h-[calc(100vh-5.5rem)] flex-col justify-between overflow-hidden animate-fade-in">
      {/* ── TOP: Header & Quick Actions */}
      <div className="shrink-0 space-y-3.5">
        {/* ── Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrcLogoBadge size="sm" />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 leading-none mb-1">
                {greeting()}
              </p>
              <h1 className="text-lg md:text-xl font-black text-slate-900 leading-tight">
                {firstName || 'Auditor'} 👋
              </h1>
            </div>
          </div>
          <button
            onClick={() => setProfileOpen(true)}
            aria-label="User Profile"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-xs font-black text-emerald-800 shadow-sm hover:border-emerald-500 hover:shadow-md active:scale-95 transition cursor-pointer"
          >
            {initials}
          </button>
        </div>

        {/* ── Quick actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/assets')}
            className="flex items-center gap-3 rounded-2xl border border-slate-200/90 bg-white p-3.5 text-left shadow-card transition-all hover:border-slate-300 hover:shadow-card-hover active:scale-[0.98] cursor-pointer"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <Search className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-black text-slate-900 leading-tight">Search Assets</p>
              <p className="text-[11px] font-medium text-slate-500 leading-tight mt-0.5">Find & filter registry</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-3 rounded-2xl border border-slate-200/90 bg-white p-3.5 text-left shadow-card transition-all hover:border-slate-300 hover:shadow-card-hover active:scale-[0.98] cursor-pointer"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <LayoutDashboard className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-black text-slate-900 leading-tight">Dashboard</p>
              <p className="text-[11px] font-medium text-slate-500 leading-tight mt-0.5">Stats & overview</p>
            </div>
          </button>
        </div>
      </div>

      {/* ── MIDDLE: Recently Added Assets */}
      <div className="my-3 flex min-h-0 flex-1 flex-col overflow-hidden space-y-2">
        <div className="flex shrink-0 items-center justify-between px-1">
          <h2 className="text-[11px] font-black uppercase tracking-wider text-slate-500">
            Recently Added Assets
          </h2>
          <button
            onClick={() => navigate('/assets')}
            className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 transition cursor-pointer"
          >
            View all →
          </button>
        </div>

        {recent.isLoading && (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-200/60" />
            ))}
          </div>
        )}

        {recent.data?.items.length === 0 && !recent.isLoading && (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200/80 bg-white py-6 text-center p-4 shadow-sm">
            <QrCode className="h-7 w-7 text-slate-400" />
            <div>
              <p className="text-xs font-bold text-slate-800">No assets recorded yet</p>
              <p className="text-[11px] text-slate-500">Scan an asset QR code to register your first tag</p>
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto space-y-2.5 pr-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {recent.data?.items.slice(0, 4).map((asset) => {
            const status = lookups?.statuses.find((s) => s.code === asset.status_code)
            const condition = lookups?.conditions.find((c) => c.code === asset.condition_code)
            return (
              <button
                key={asset.id}
                onClick={() => navigate(`/assets/${asset.id}`)}
                className="group flex w-full items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-3 text-left shadow-card transition-all hover:border-slate-300 hover:shadow-card-hover active:scale-[0.99] cursor-pointer"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                  {asset.image_count === 0 ? (
                    <ImageOff className="h-4 w-4 text-slate-400" />
                  ) : (
                    <QrCode className="h-4 w-4 text-emerald-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {asset.asset_name && asset.asset_name.trim().toLowerCase() !== asset.asset_code.trim().toLowerCase() ? (
                    <>
                      <span className="font-mono text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                        {asset.asset_code}
                      </span>
                      <p className="truncate text-xs font-bold text-slate-900 leading-tight mt-0.5 group-hover:text-emerald-950 transition-colors">
                        {asset.asset_name}
                      </p>
                    </>
                  ) : (
                    <p className="font-mono truncate text-xs font-bold text-slate-900 leading-tight">
                      {asset.asset_code}
                    </p>
                  )}
                  <div className="mt-1 flex gap-1.5">
                    <Badge label={status?.label ?? humanise(asset.status_code)} semantic={status?.semantic} />
                    <Badge label={condition?.label ?? humanise(asset.condition_code)} semantic={condition?.semantic} />
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )
          })}
        </div>
      </div>

      {/* ── BOTTOM: Scan CTA (Thumb-reachable) */}
      <div className="shrink-0 pt-1 pb-1">
        <button
          onClick={() => navigate('/scan')}
          className="relative flex w-full items-center justify-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-tr from-[#053736] via-[#074746] to-emerald-600 py-3.5 sm:py-4 shadow-[0_6px_25px_rgba(5,55,54,0.35)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(5,55,54,0.45)] active:scale-[0.98] cursor-pointer"
        >
          <div className="relative flex items-center gap-3">
            <div className="rounded-xl bg-white/15 p-2 backdrop-blur-sm border border-white/20 shadow-inner">
              <QrCode className="h-6 w-6 text-white stroke-[2.2]" />
            </div>
            <div className="text-left">
              <span className="block text-lg font-black tracking-wider text-white leading-tight">
                SCAN ASSET TAG
              </span>
              <span className="flex items-center gap-1 text-[11.5px] font-bold text-emerald-200">
                <Zap className="h-3 w-3 fill-current" /> Instant QR & Camera Capture
              </span>
            </div>
          </div>
        </button>
      </div>

      {/* ── User Profile & Logout Modal */}
      <UserProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  )
}
